/**
 * StickyAtc — custom element for the sticky "Add to Cart" bar.
 *
 * Depends on (loaded before this file):
 *   - constants.js  → PUB_SUB_EVENTS
 *   - global.js     → subscribe(), publish()
 *
 * HTML contract: see sticky-atc.liquid snippet.
 */

class StickyAtc extends HTMLElement {
  // Holds the unsubscriber returned by subscribe() so we can clean up
  // in disconnectedCallback and avoid memory leaks.
  _unsubscribeOptionChange = undefined;

  // Holds the IntersectionObserver watching the main ATC button.
  _atcObserver = undefined;

  connectedCallback() {
    try {
      this._init();
    } catch (err) {
      console.error('[StickyAtc] Error during init:', err);
    }
  }

  disconnectedCallback() {
    // Unsubscribe from pub/sub to prevent callbacks firing on a detached element.
    if (typeof this._unsubscribeOptionChange === 'function') {
      this._unsubscribeOptionChange();
    }
    if (typeof this._unsubscribeVariantChange === 'function') {
      this._unsubscribeVariantChange();
    }

    if (this._atcObserver) {
      this._atcObserver.disconnect();
    }
    if (this._loadingObserver) {
      this._loadingObserver.disconnect();
    }

    // Remove global keyboard listener.
    document.removeEventListener('keydown', this._onKeyDown);
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  _init() {
    const sectionId = this.dataset.section;

    // Locate key DOM nodes we interact with repeatedly.
    this._bar = this.querySelector('.sticky-atc__bar');
    this._sizeTrigger = this.querySelector('.sticky-atc__size-trigger');
    this._sizeLabel = this.querySelector('.sticky-atc__size-label');
    this._submitBtn = this.querySelector('.sticky-atc__submit');
    this._image = this.querySelector('.sticky-atc__image');
    this._popover = this.querySelector(`#StickyATCPopover-${sectionId}`);
    this._overlay = this.querySelector('.sticky-atc__popover-overlay');

    // Main picker elements (outside this custom element, inside the product section).
    this._variantSelects = document.getElementById(this.dataset.variantSelectsId);
    this._mainAtcButton = document.getElementById(`ProductSubmitButton-${sectionId}`);

    this._setupScrollVisibility();
    this._setupSizeLabel();
    this._setupPopover();
    this._buildMirror();
    this._setupSubmitButton();
    this._subscribeToVariantChanges();
  }

  // ─── 1. Show / hide on scroll ─────────────────────────────────────────────

  _setupScrollVisibility() {
    if (!this._mainAtcButton) {
      // Main button not found; keep bar visible as a safe fallback.
      this.classList.add('sticky-atc--visible');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Browser does not support IntersectionObserver; keep bar permanently visible.
      this.classList.add('sticky-atc--visible');
      return;
    }

    this._atcObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.classList.remove('sticky-atc--visible');
          this._closePopover();
        } else {
          // Only show when scrolled past the button (above viewport), not when approaching from top.
          const scrolledPast = entry.boundingClientRect.top < 0;
          this.classList.toggle('sticky-atc--visible', scrolledPast);
          if (!scrolledPast) this._closePopover();
        }
      });
    });

    this._atcObserver.observe(this._mainAtcButton);
  }

  // ─── 2. Size label display ────────────────────────────────────────────────

  /**
   * Reads the currently selected values from the main variant-selects element
   * and returns them as an ordered array of { name, position, value } objects.
   *
   * The main picker uses radio inputs named "OptionName-Position"
   * (e.g. "Band Size-2") and <select> elements for dropdown-style options.
   */
  _readSelectedOptions() {
    if (!this._variantSelects) return [];

    // Gather all checked radios and all selects in document order.
    const inputs = this._variantSelects.querySelectorAll(
      'input[type="radio"]:checked, select'
    );

    const options = [];

    inputs.forEach((input) => {
      if (input.tagName === 'SELECT') {
        // name attribute on <select> follows the same "OptionName-Position" convention.
        const { name, value } = input;
        if (!name || !value) return;
        const lastDash = name.lastIndexOf('-');
        if (lastDash === -1) return;
        const optionName = name.substring(0, lastDash);
        const position = parseInt(name.substring(lastDash + 1), 10);
        options.push({ name: optionName, position, value });
      } else {
        // Radio input. name = "OptionName-Position", value = option value.
        const { name, value } = input;
        if (!name || !value) return;
        const lastDash = name.lastIndexOf('-');
        if (lastDash === -1) return;
        const optionName = name.substring(0, lastDash);
        const position = parseInt(name.substring(lastDash + 1), 10);
        options.push({ name: optionName, position, value });
      }
    });

    // Sort by position so the label always reads left-to-right (e.g. 1 / 2 / 3).
    options.sort((a, b) => a.position - b.position);
    return options;
  }

  /**
   * Builds the human-readable label string and updates both the trigger button
   * label and the per-option selected-value spans inside the popover.
   */
  _updateSizeLabel() {
    const selectedOptions = this._readSelectedOptions();

    // Build label from all selected options (including Color for now).
    const MAX_LABEL_LENGTH = 10;
    const fullLabel = selectedOptions.map((o) => o.value).join(' / ');
    const labelText = fullLabel.length > MAX_LABEL_LENGTH
      ? fullLabel.slice(0, MAX_LABEL_LENGTH).trimEnd() + '…'
      : fullLabel;

    if (this._sizeLabel) {
      this._sizeLabel.textContent = labelText;
    }

  }

  _setupSizeLabel() {
    this._updateSizeLabel();
  }

  // ─── 3. Popover open / close ──────────────────────────────────────────────

  _setupPopover() {
    if (!this._sizeTrigger || !this._popover) return;

    // Bound reference so we can call removeEventListener correctly if needed.
    this._onKeyDown = this._handleKeyDown.bind(this);

    this._sizeTrigger.addEventListener('click', () => this._togglePopover());

    if (this._overlay) {
      this._overlay.addEventListener('click', () => this._closePopover());
    }

    document.addEventListener('keydown', this._onKeyDown);
  }

  _togglePopover() {
    const isOpen = !this._popover.hasAttribute('hidden');
    if (isOpen) {
      this._closePopover();
    } else {
      this._openPopover();
    }
  }

  _openPopover() {
    if (!this._popover) return;
    this._popover.removeAttribute('hidden');
    this._popover.setAttribute('aria-hidden', 'false');
    this._popover.classList.add('sticky-atc__popover--open');
    if (this._sizeTrigger) this._sizeTrigger.setAttribute('aria-expanded', 'true');
    if (this._overlay) this._overlay.classList.add('sticky-atc__popover-overlay--visible');
  }

  _closePopover() {
    if (!this._popover) return;
    this._popover.setAttribute('hidden', '');
    this._popover.setAttribute('aria-hidden', 'true');
    this._popover.classList.remove('sticky-atc__popover--open');
    if (this._sizeTrigger) this._sizeTrigger.setAttribute('aria-expanded', 'false');
    if (this._overlay) this._overlay.classList.remove('sticky-atc__popover-overlay--visible');
  }

  _handleKeyDown(event) {
    if (event.key === 'Escape') {
      this._closePopover();
      // Return focus to the trigger so the user is not left stranded.
      if (this._sizeTrigger) this._sizeTrigger.focus();
    }
  }

  // ─── 4. Mirror variant-selects into popover ──────────────────────────────

  _buildMirror() {
    if (!this._variantSelects || !this._popover) return;

    const inner = this._popover.querySelector('.sticky-atc__popover-inner');
    if (!inner) return;

    // Clone innerHTML into a plain div — avoids re-running the variant-selects
    // custom element's connectedCallback and double pub/sub publishing.
    this._mirror = document.createElement('div');
    this._mirror.className = 'sticky-atc__variant-selects-mirror';
    this._mirror.innerHTML = this._variantSelects.innerHTML;

    // Drop the embedded selected-variant JSON script — not needed here.
    this._mirror.querySelector('script[data-selected-variant]')?.remove();

    // Prefix all id attributes to prevent duplicate ids in the document.
    this._mirror.querySelectorAll('[id]').forEach((el) => {
      el.id = `satc-${el.id}`;
    });
    this._mirror.querySelectorAll('[for]').forEach((el) => {
      el.setAttribute('for', `satc-${el.getAttribute('for')}`);
    });

    // Detach from the product form — these inputs are visual only.
    this._mirror.querySelectorAll('[form]').forEach((el) => el.removeAttribute('form'));

    // Hide Color option group for this phase.
    this._mirror.querySelector('.Option-Color')?.setAttribute('style', 'display:none');

    // Relay changes in the mirror back to the original variant-selects.
    // Use label.click() / input.click() so the browser generates the full
    // native event chain (checked state change + change event) — identical to
    // a real user click. Dispatching a synthetic 'change' directly on the
    // input skips the click chain that product-info relies on for URL updates.
    // _relaying prevents the resulting pub/sub from looping back into _syncMirrorState.
    this._mirror.addEventListener('change', (e) => {
      if (this._relaying) return;
      this._relaying = true;

      const el = e.target;
      const origInputs = Array.from(this._variantSelects.querySelectorAll('input[type="radio"]'));
      const origSelects = Array.from(this._variantSelects.querySelectorAll('select'));

      if (el.type === 'radio') {
        const orig = origInputs.find(
          (r) => r.name.trim() === el.name.trim() && r.value === el.value
        );
        if (orig) {
          // product-info.js calls `document.querySelector('#id').focus()` after
          // every AJAX fetch to restore keyboard context (renderProductInfo line ~155).
          // That focus scrolls the page back to the variant picker at the top.
          // Intercept it in the capture phase and immediately restore our position.
          const savedScrollY = window.scrollY;
          const origId = orig.id;

          const focusGuard = (ev) => {
            if (ev.target.id !== origId) return;
            document.removeEventListener('focus', focusGuard, { capture: true });
            // Re-scroll synchronously — both ops coalesce before the next paint.
            window.scrollTo(0, savedScrollY);
          };
          document.addEventListener('focus', focusGuard, { capture: true });
          // Safety cleanup if the fetch fails and focus never fires.
          setTimeout(() => document.removeEventListener('focus', focusGuard, { capture: true }), 5000);

          orig.checked = true;
          orig.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (el.tagName === 'SELECT') {
        const orig = origSelects.find((s) => s.name.trim() === el.name.trim());
        if (orig) {
          orig.value = el.value;
          orig.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this._relaying = false;
      this._closePopover();
    });

    inner.appendChild(this._mirror);

    // Seed the mirror with the current checked states from the original.
    this._syncMirrorState();
  }

  _rebuildMirror() {
    // viewTransition swaps the entire variant-selects element in the DOM,
    // so our cached reference now points to the hidden/removed old node.
    // Re-acquire before rebuilding so the mirror and relay use the live element.
    this._variantSelects = document.getElementById(this.dataset.variantSelectsId);

    if (!this._popover) return;
    const inner = this._popover.querySelector('.sticky-atc__popover-inner');
    if (!inner) return;
    inner.innerHTML = '';
    this._mirror = null;
    this._buildMirror();
  }

  // Copies checked/selected state from the original variant-selects to the mirror.
  _syncMirrorState() {
    if (!this._mirror || !this._variantSelects) return;

    this._variantSelects.querySelectorAll('input[type="radio"]:checked').forEach((orig) => {
      Array.from(this._mirror.querySelectorAll('input[type="radio"]'))
        .find((r) => r.name.trim() === orig.name.trim() && r.value === orig.value)
        ?.setAttribute('checked', '');
    });

    // For radios we need to set the property, not just the attribute.
    this._variantSelects.querySelectorAll('input[type="radio"]:checked').forEach((orig) => {
      const mirrorRadio = Array.from(this._mirror.querySelectorAll('input[type="radio"]'))
        .find((r) => r.name.trim() === orig.name.trim() && r.value === orig.value);
      if (mirrorRadio) mirrorRadio.checked = true;
    });

    this._variantSelects.querySelectorAll('select').forEach((orig) => {
      const mirrorSelect = Array.from(this._mirror.querySelectorAll('select'))
        .find((s) => s.name.trim() === orig.name.trim());
      if (mirrorSelect) mirrorSelect.value = orig.value;
    });

    this._updateMirrorLabels();
  }

  // Updates the selected-value display inside each option group legend.
  // Swatch-type options already have a [data-selected-value] span from the theme;
  // pill/button-type options don't — we create one and add a colon separator.
  _updateMirrorLabels() {
    if (!this._mirror) return;

    this._mirror.querySelectorAll('fieldset.product-form__input').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend.form__label');
      if (!legend) return;

      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
      const select = fieldset.querySelector('select');
      const selectedValue = checkedRadio?.value ?? select?.value ?? '';

      let valueSpan = legend.querySelector('[data-selected-value]');
      if (!valueSpan) {
        // Pill/button-type legends have no span — add ": value" after the name text.
        legend.appendChild(document.createTextNode(': '));
        valueSpan = document.createElement('span');
        valueSpan.setAttribute('data-selected-value', '');
        legend.appendChild(valueSpan);
      }
      valueSpan.textContent = selectedValue;
    });
  }

  // ─── 5. ATC button ───────────────────────────────────────────────────────

  _setupSubmitButton() {
    if (!this._submitBtn) return;

    this._submitBtn.addEventListener('click', () => {
      // Mirror the aria-disabled guard from ProductForm.onSubmitHandler.
      if (this._submitBtn.getAttribute('aria-disabled') === 'true') return;

      const productFormId = this._submitBtn.dataset.productFormId;
      const form = document.getElementById(productFormId);
      if (!form) {
        return;
      }

      // requestSubmit() fires the form's submit event so ProductForm's
      // onSubmitHandler runs (AJAX cart add, loading state on the main button).
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    });

    // Mirror the main button's loading state onto our sticky button.
    // ProductForm manages loading via .loading class and aria-disabled on
    // #ProductSubmitButton-{sectionId} — observe those mutations directly.
    this._setupLoadingMirror();
  }

  _setupLoadingMirror() {
    if (!this._mainAtcButton || !this._submitBtn) return;

    this._loadingObserver = new MutationObserver(() => {
      const isLoading = this._mainAtcButton.classList.contains('loading');
      const isDisabled = this._mainAtcButton.getAttribute('aria-disabled') === 'true';

      this._submitBtn.classList.toggle('loading', isLoading);
      this._submitBtn.toggleAttribute('aria-disabled', isDisabled);

      // Keep the spinner visibility in sync with the loading class.
      const spinner = this._submitBtn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    });

    this._loadingObserver.observe(this._mainAtcButton, {
      attributes: true,
      attributeFilter: ['class', 'aria-disabled'],
    });
  }

  // ─── 6. Pub/sub subscription ──────────────────────────────────────────────

  _subscribeToVariantChanges() {
    if (typeof subscribe !== 'function') return;

    this._unsubscribeOptionChange = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      () => {
        this._updateSizeLabel();
        if (!this._relaying) this._syncMirrorState();
      }
    );

    // After Shopify's AJAX section render replaces variant-selects innerHTML,
    // the mirror is stale. Rebuild it so the two stay in sync.
    this._unsubscribeVariantChange = subscribe(
      PUB_SUB_EVENTS.variantChange,
      (event) => {
        this._updateStickyImage(event?.data?.variant);
        if (!this._relaying) this._rebuildMirror();
      }
    );
  }

  // Swaps the sticky bar thumbnail to the newly selected variant's image.
  // The variant object (Shopify variant JSON) exposes featured_image / featured_media.
  _updateStickyImage(variant) {
    if (!variant || !this._image) return;

    const src =
      variant.featured_image?.src ||
      variant.featured_media?.preview_image?.src;
    if (!src) return;

    // Request a 200w render to match the initial Liquid image_url width.
    this._image.src = src.includes('?') ? `${src}&width=200` : `${src}?width=200`;

    const alt = variant.featured_image?.alt ?? variant.featured_media?.alt;
    if (alt) this._image.alt = alt;
  }
}

customElements.define('sticky-atc', StickyAtc);
